import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Save, 
  Loader2, 
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Mail,
  Eye,
  FileText
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEmailTemplatePage = () => {
  // Email template state
  const [template, setTemplate] = useState({
    subject: '',
    heading: '',
    paragraph1: '',
    paragraph2: '',
    paragraph3: '',
    button_text: '',
    footer: ''
  });
  
  // Landing page text state
  const [landingText, setLandingText] = useState({
    heading: '',
    description: '',
    greeting: '',
    button_text: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('email');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Load both email template and landing text in parallel
      const [emailRes, landingRes] = await Promise.all([
        axios.get(`${API}/admin/email-template`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/survey-landing-text`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTemplate(emailRes.data.template || {});
      setLandingText(landingRes.data.text || {});
    } catch (err) {
      setError('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/admin/email-template`, 
        { template },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Email-skabelon gemt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLanding = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/admin/survey-landing-text`, 
        { text: landingText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Landingsside-tekst gemt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  const handleResetEmail = async () => {
    if (!window.confirm('Vil du nulstille email-skabelonen til standard? Dette kan ikke fortrydes.')) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/admin/email-template/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data.template || {});
      setSuccess('Email-skabelon nulstillet');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Kunne ikke nulstille');
    } finally {
      setSaving(false);
    }
  };

  const handleResetLanding = async () => {
    if (!window.confirm('Vil du nulstille landingsside-teksten til standard? Dette kan ikke fortrydes.')) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/admin/survey-landing-text/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLandingText(response.data.text || {});
      setSuccess('Landingsside-tekst nulstillet');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Kunne ikke nulstille');
    } finally {
      setSaving(false);
    }
  };

  const updateTemplateField = (field, value) => {
    setTemplate(prev => ({ ...prev, [field]: value }));
  };

  const updateLandingField = (field, value) => {
    setLandingText(prev => ({ ...prev, [field]: value }));
  };

  // Preview with placeholder replaced
  const previewText = (text) => {
    return text
      .replace(/{fornavn}/g, 'Anders')
      .replace(/{navn}/g, 'Anders Hansen');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-outfit">Tekst & Email</h1>
        <p className="text-slate-500 text-sm mt-1">
          Rediger tekst i email og på survey-landingsside. Brug <code className="bg-slate-100 px-1 rounded">{'{fornavn}'}</code> for kundens fornavn.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-lg text-red-600">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-600">
          <CheckCircle2 className="h-4 w-4" />
          {success}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email-skabelon
          </TabsTrigger>
          <TabsTrigger value="landing" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Landingsside
          </TabsTrigger>
        </TabsList>

        {/* EMAIL TEMPLATE TAB */}
        <TabsContent value="email" className="mt-6">
          <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? 'Skjul preview' : 'Vis preview'}
            </Button>
            <Button variant="outline" onClick={handleResetEmail} disabled={saving}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Nulstil
            </Button>
            <Button onClick={handleSaveEmail} disabled={saving} className="bg-slate-900">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Gem email
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Editor */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Email-indhold</CardTitle>
                <CardDescription>Den email kunden modtager</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-700">Emne</Label>
                  <Input
                    value={template.subject || ''}
                    onChange={(e) => updateTemplateField('subject', e.target.value)}
                    placeholder="Email emnelinje..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Overskrift</Label>
                  <Input
                    value={template.heading || ''}
                    onChange={(e) => updateTemplateField('heading', e.target.value)}
                    placeholder="Overskrift i emailen..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Afsnit 1</Label>
                  <Textarea
                    value={template.paragraph1 || ''}
                    onChange={(e) => updateTemplateField('paragraph1', e.target.value)}
                    placeholder="Første afsnit..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Afsnit 2</Label>
                  <Textarea
                    value={template.paragraph2 || ''}
                    onChange={(e) => updateTemplateField('paragraph2', e.target.value)}
                    placeholder="Andet afsnit..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Afsnit 3</Label>
                  <Textarea
                    value={template.paragraph3 || ''}
                    onChange={(e) => updateTemplateField('paragraph3', e.target.value)}
                    placeholder="Tredje afsnit..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Knaptekst</Label>
                  <Input
                    value={template.button_text || ''}
                    onChange={(e) => updateTemplateField('button_text', e.target.value)}
                    placeholder="Tekst på knappen..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Fodtekst</Label>
                  <Textarea
                    value={template.footer || ''}
                    onChange={(e) => updateTemplateField('footer', e.target.value)}
                    placeholder="Lille tekst i bunden..."
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Email Preview */}
            {showPreview && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Email preview</CardTitle>
                  <CardDescription>Sådan ser emailen ud for kunden</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                      <p className="text-xs text-slate-500">Fra: Gardinbussen</p>
                      <p className="text-xs text-slate-500">Emne:</p>
                      <p className="text-sm font-medium text-slate-900">{previewText(template.subject || '')}</p>
                    </div>
                    
                    <div className="p-6">
                      <div className="bg-slate-50 rounded-xl p-6 mb-4">
                        <h1 className="text-xl font-bold text-slate-900 mb-4">
                          {previewText(template.heading || '')}
                        </h1>
                        {template.paragraph1 && (
                          <p className="text-slate-600 mb-3">{previewText(template.paragraph1)}</p>
                        )}
                        {template.paragraph2 && (
                          <p className="text-slate-600 mb-3">{previewText(template.paragraph2)}</p>
                        )}
                        {template.paragraph3 && (
                          <p className="text-slate-600 mb-4">{previewText(template.paragraph3)}</p>
                        )}
                        <button className="bg-slate-900 text-white px-6 py-3 rounded-lg font-medium">
                          {previewText(template.button_text || 'Start')}
                        </button>
                      </div>
                      {template.footer && (
                        <p className="text-sm text-slate-400">{previewText(template.footer)}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* LANDING PAGE TAB */}
        <TabsContent value="landing" className="mt-6">
          <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" onClick={handleResetLanding} disabled={saving}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Nulstil
            </Button>
            <Button onClick={handleSaveLanding} disabled={saving} className="bg-slate-900">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Gem landingsside
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Landing Editor */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Landingsside-tekst</CardTitle>
                <CardDescription>Den side kunden ser når de klikker på linket</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-700">Overskrift</Label>
                  <Input
                    value={landingText.heading || ''}
                    onChange={(e) => updateLandingField('heading', e.target.value)}
                    placeholder="Hovedoverskrift..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Beskrivelse</Label>
                  <Textarea
                    value={landingText.description || ''}
                    onChange={(e) => updateLandingField('description', e.target.value)}
                    placeholder="Beskrivende tekst..."
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-700">Hilsen</Label>
                  <Input
                    value={landingText.greeting || ''}
                    onChange={(e) => updateLandingField('greeting', e.target.value)}
                    placeholder="Hej {fornavn}, det tager ca. 1 minut."
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">Brug {'{fornavn}'} for kundens fornavn</p>
                </div>

                <div>
                  <Label className="text-slate-700">Knaptekst</Label>
                  <Input
                    value={landingText.button_text || ''}
                    onChange={(e) => updateLandingField('button_text', e.target.value)}
                    placeholder="Tekst på start-knappen..."
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Landing Preview */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Preview</CardTitle>
                <CardDescription>Sådan ser landingssiden ud</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_72e2165b-e90b-4d70-9b27-bab6784408f0/artifacts/9iij87hr_Gardinbussen.org%20logo%20%281%29.png" 
                    alt="Gardinbussen.org"
                    className="h-10 mb-4 object-contain"
                  />
                  
                  <h1 className="text-xl font-bold text-slate-900 mb-2 font-outfit">
                    {landingText.heading || 'Overskrift'}
                  </h1>
                  
                  <p className="text-slate-600 mb-6 text-sm">
                    {landingText.description || 'Beskrivelse...'}
                  </p>
                  
                  <p className="text-slate-500 text-xs mb-4">
                    {previewText(landingText.greeting || 'Hej {fornavn}, det tager ca. 1 minut.')}
                  </p>
                  
                  <button className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium text-sm">
                    {landingText.button_text || 'Start'} →
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminEmailTemplatePage;
